import React, { useState, useEffect } from 'react';
import { Printer } from 'lucide-react';
import RefertiProcedura from './RefertiProcedura';
import BottoneRefertoDaProcedura from './BottoneRefertoDaProcedura';
import ModuloFoto from './ModuloFoto';
import ModuloNote from './ModuloNote';
import ModuloParametri from './ModuloParametri';
import ModuloBotox from './ModuloBotox';
import ModuloFiller from './ModuloFiller';
import ModuloZone from './ModuloZone';
import api, { dettaglioAPI } from '../../services/api';
import '../../styles/ProceduraDettaglio.css';
import SomministrazioniVisita from './SomministrazioniVisita';
import { DocumentiGenerati, DocumentiCaricati } from './DocumentiProcedura';
import SchedeProcedura from './SchedeProcedura';
import { Sezione } from './SezioneProcedura';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { notificaErrore, messaggioDa } from '../../utils/notifiche';
import { annunciaProcedureCambiate } from '../../utils/annunci';

const fmtData = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

const Ph = ({ testo }) => (
    <div className="pd-ph">
        <span className="pd-ph-badge">in costruzione</span>
        {testo || 'Sezione non ancora attiva.'}
    </div>
);

/* Campo di testo editabile con salvataggio per sezione */
const CampoEditabile = ({ visitaId, campo, valoreIniziale, placeholder, hint }) => {
    const [val, setVal] = useState(valoreIniziale || '');
    const [saving, setSaving] = useState(false);
    const [salvato, setSalvato] = useState(false);
    /* «Modificato» e' il confronto con l'ultimo valore caricato o
       salvato, non un flag: vedi utils/uscitaProtetta.js. Ogni campo
       si protegge da se', e la domanda all'uscita e' una sola anche
       con piu' campi sporchi. */
    const [versione, setVersione] = useState(0);
    const dirty = useSporco(val, versione);
    useUscitaProtetta(dirty);

    useEffect(() => { setVal(valoreIniziale || ''); setVersione((v) => v + 1); }, [valoreIniziale]);

    const onChange = (e) => { setVal(e.target.value); setSalvato(false); };

    const salva = async () => {
        setSaving(true);
        try {
            await dettaglioAPI.saveCampo(visitaId, campo, val);
            setSalvato(true); setVersione((v) => v + 1);
            /* Il riepilogo mostra questi riquadri sotto «Anamnesi /
               Profilo medico»: senza l'annuncio li vedrebbe alla
               prossima apertura della scheda, non adesso. */
            annunciaProcedureCambiate();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a salvare il campo'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="pd-campo">
            {hint && <div className="pd-campo-hint">{hint}</div>}
            <textarea className="pd-campo-ta" rows="3" value={val} onChange={onChange} placeholder={placeholder} />
            <div className="pd-campo-actions">
                {salvato && !dirty && <span className="pd-campo-ok">✓ Salvato</span>}
                <button className="pd-campo-save" onClick={salva} disabled={saving || !dirty}>
                    {saving ? 'Salvataggio…' : 'Salva'}
                </button>
            </div>
        </div>
    );
};

/**
 * «Stampa»: la procedura su carta, com'e'. Cosa entra e cosa resta fuori
 * (note mediche, accettazione, fotografie) lo decide il server, in
 * services/pdfProcedura.js. Sta in basso a destra, dove uno arriva
 * dopo aver riletto tutto: e' li' che l'ha cercata chi la voleva.
 */
const BottoneStampaProcedura = ({ visitaId }) => {
    const [inCorso, setInCorso] = useState(false);
    const stampa = async () => {
        setInCorso(true);
        try {
            const x = await api.get(`/visite/${visitaId}/stampa-pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([x.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 30000);
        } catch (e) {
            /* La risposta e' un blob anche quando e' un errore: il
               messaggio del server va letto dal blob prima di mostrarlo. */
            let messaggio = 'Non riesco a stampare la procedura';
            try {
                const testo = await e?.response?.data?.text?.();
                if (testo) messaggio = JSON.parse(testo).message || messaggio;
            } catch (_) { /* resta il ripiego */ }
            notificaErrore(messaggio);
        } finally {
            setInCorso(false);
        }
    };
    return (
        <button type="button" className="pd-stampa" onClick={stampa} disabled={inCorso}
                title="Stampa di consultazione: non è un documento firmato. Per un documento con valore, il referto.">
            <Printer size={15} strokeWidth={1.8} aria-hidden="true" />
            {inCorso ? 'Preparo la stampa…' : 'Stampa'}
        </button>
    );
};

const ProceduraDettaglio = ({ visita, moduli, onAddModulo, onReload, sessoPaziente }) => {
    const [dati, setDati] = useState({});

    useEffect(() => {
        let alive = true;
        dettaglioAPI.get(visita.id)
            .then(r => { if (alive && r.data.success) setDati(r.data.data.dati || {}); })
            .catch(() => {});
        return () => { alive = false; };
    }, [visita.id]);

    const byTipo = (t) => (moduli || []).filter(m => m.tipo === t);

    const addBtn = (tipo, label) => (
        <button className="pd-add" onClick={() => onAddModulo(visita.id, tipo)} title={`Aggiungi ${label}`}>
            + {label}
        </button>
    );

    const renderModuli = (tipo, Comp) => byTipo(tipo).map(m => (
        <div key={m.id} className="pd-modulo">
            {/* `pazienteId` serve alle fotografie: si caricano sulla rotta
                delle foto del paziente, indicando la procedura. */}
            <Comp modulo={m} visitaId={visita.id} pazienteId={visita.paziente_id}
                  onUpdate={onReload} sessoPaziente={sessoPaziente} />
        </div>
    ));

    const nSchemi = byTipo('botox').length + byTipo('filler').length + byTipo('zone').length;

    return (
        <div className="pd-wrap">
            <div className="pd-cols">
                {/* ---------- COLONNA SINISTRA ---------- */}
                <div className="pd-col">
                    <Sezione title="Accettazione paziente">
                        <CampoEditabile visitaId={visita.id} campo="accettazione"
                            valoreIniziale={dati.accettazione} placeholder="Dati di accettazione del paziente…" />
                    </Sezione>

                    <Sezione title="Note anamnestiche / patologie associate">
                        <CampoEditabile visitaId={visita.id} campo="note_anamnestiche"
                            valoreIniziale={dati.note_anamnestiche} placeholder="Anamnesi, patologie associate, allergie rilevanti…" />
                    </Sezione>

                    <Sezione title="Esame obiettivo / diagnosi" defaultOpen>
                        <CampoEditabile visitaId={visita.id} campo="esame_obiettivo"
                            valoreIniziale={dati.esame_obiettivo}
                            placeholder="Esame obiettivo e diagnosi…"
                            hint={visita.patologia ? `Patologia registrata: ${visita.patologia}` : null} />
                    </Sezione>

                    <Sezione title="Finalità terapeutiche">
                        <CampoEditabile visitaId={visita.id} campo="finalita_terapeutiche"
                            valoreIniziale={dati.finalita_terapeutiche} placeholder="Obiettivi del trattamento…" />
                    </Sezione>

                    <Sezione title="Schemi tecnici"
                             right={addBtn('zone', 'Zone trattate')} defaultOpen>
                        {nSchemi === 0
                            ? <Ph testo="Nessuno schema tecnico. Usa + Zone trattate per segnare dove sei intervenuto." />
                            : <>{renderModuli('botox', ModuloBotox)}{renderModuli('filler', ModuloFiller)}{renderModuli('zone', ModuloZone)}</>}
                    </Sezione>

                    <Sezione title="Parametri trattamento" right={addBtn('parametri', 'Parametri')}>
                        {byTipo('parametri').length === 0
                            ? <Ph testo="Nessun parametro registrato." />
                            : renderModuli('parametri', ModuloParametri)}
                    </Sezione>

                    <Sezione title="Fotografie" right={addBtn('foto', 'Foto')}>
                        {byTipo('foto').length === 0
                            ? <Ph testo="Nessuna foto in questa procedura." />
                            : renderModuli('foto', ModuloFoto)}
                    </Sezione>

                    <Sezione title="Registrazioni audio/video" placeholder>
                        <Ph />
                    </Sezione>

                    {/* Le schede del medico di questa visita: solo nella
                        cartella delle prestazioni mediche. I campi
                        personalizzati sono la ragione per cui quella cartella
                        esiste; in quella estetica ci sono gli schemi tecnici,
                        che sono l'equivalente. Non disegna niente se il medico
                        non ha schede attive, quindi per chi non le usa la
                        colonna resta com'era. */}
                    {(visita.cartella || 'mediche') !== 'estetica' && (
                        <SchedeProcedura visita={visita} />
                    )}
                </div>

                {/* ---------- COLONNA DESTRA ---------- */}
                <div className="pd-col">
                    <Sezione title="Annotazioni / Note" right={addBtn('note', 'Nota')} defaultOpen>
                        <CampoEditabile visitaId={visita.id} campo="annotazioni"
                            valoreIniziale={dati.annotazioni}
                            placeholder="Annotazioni sulla procedura…"
                            hint={visita.note_generali ? `Note generali: ${visita.note_generali}` : null} />
                        {byTipo('note').length > 0 && <div className="pd-moduli-note">{renderModuli('note', ModuloNote)}</div>}
                    </Sezione>

                    <Sezione title="Note mediche">
                        <CampoEditabile visitaId={visita.id} campo="note_mediche"
                            valoreIniziale={dati.note_mediche} placeholder="Note mediche riservate…" />
                    </Sezione>

                    {/* Referti e ricette nati da questa procedura: entrambi
                        documenti di testo nell'editor dei referti. Le azioni
                        stanno dentro la sezione e non nella testa, perche'
                        sono due e la testa e' stretta. */}
                    <Sezione title="Referti / Prescrizioni" defaultOpen>
                        <RefertiProcedura visita={visita}
                                          azioni={<BottoneRefertoDaProcedura visitaId={visita.id} />} />
                    </Sezione>

                    <Sezione title="Appuntamento" defaultOpen>
                        <div className="pd-appt">
                            <div className="pd-appt-data">{fmtData(visita.data_visita)}</div>
                            {visita.trattamento && <div className="pd-appt-tratt">{visita.trattamento}</div>}
                            {visita.medico_cognome && (
                                <div className="pd-appt-medico">Dr. {visita.medico_cognome} {visita.medico_nome}</div>
                            )}
                            {visita.patologia && <div className="pd-appt-pat">Diagnosi: {visita.patologia}</div>}
                        </div>
                    </Sezione>

                    {/* I consensi nati da un modello e legati a QUESTA
                        prestazione: si generano da qui perche' e' qui che si
                        sa a quale procedura appartengono. */}
                    <Sezione title="Documenti generati / genera documenti" defaultOpen>
                        <DocumentiGenerati visita={visita} />
                    </Sezione>

                    <Sezione title="Documenti caricati / carica documenti" defaultOpen>
                        <DocumentiCaricati visita={visita} />
                    </Sezione>

                    <Sezione icon="📦" title="Prodotti scaricati da magazzino">
                        <SomministrazioniVisita visitaId={visita?.id} pazienteId={visita?.paziente_id} />
                    </Sezione>
                </div>
            </div>

            <div className="pd-piede">
                <BottoneStampaProcedura visitaId={visita.id} />
            </div>
        </div>
    );
};

export default ProceduraDettaglio;
